// Flamecaller projectile helpers. Pure. No React, no Three.js.
//
// ── Codex (Flamecaller) ──
//   "Tap, Banish all your dead fire minions → Shoot a projectile.
//    It deals damage equal to the sum of their (F)."
//
// ── Codex (Projectile) ──
//   "A projectile's flight begins at the shooting unit's location and
//    travels within the same region in a cardinal direction. Its
//    flight continues until it either reaches the edge of its region
//    or it reaches the first unit (enemy or ally) along its path,
//    ignoring any allies at the projectile's starting location.
//    If there are multiple valid units that could be hit, the
//    projectile's controller chooses which unit the projectile hits.
//    Projectiles cannot hit minions with Stealth."
//
// The orchestrator (GameBoard) owns the actual cemetery → banished
// move, the cell-pick prompt, and the damage application. This module
// owns the read-only predicates, the (F)-sum arithmetic, and the
// projectile path scan.
//
// Catalog shape is the NORMALIZED card object emitted by
// `normalizeSorceryCard` (elements is `[{id, name}]`, fireThreshold
// is a top-level number, type is a top-level string). NOT the raw
// `public/sorcery-cards.json` shape.

// The projectile flight walker is THE shared engine/core implementation
// (codex Projectile is a core rule, not card text). Re-exported here so
// existing automation-side consumers keep their import path.
import { projectilePaths } from '../../../core/ranged.js';
import { countsAsMinion } from '../../../core/typeline';
export { projectilePaths };

function lookupCatalog(card, catalog) {
  if (!card || !catalog) return null;
  return catalog.find((c) => c?.unique_id === card.cardId) || null;
}

function hasFireElement(catalogCard) {
  const list = catalogCard?.elements || [];
  return list.some((e) => String(e?.id || e?.name || '').toLowerCase() === 'fire');
}

/** True iff `card` (a cemetery cardInstance) is a Fire Minion in the catalog. */
export function isFireMinion(card, catalog) {
  const full = lookupCatalog(card, catalog);
  if (!full) return false;
  if (!countsAsMinion(full)) return false;
  return hasFireElement(full);
}

/** The card's printed Fire threshold (used as its (F) value for the sum). */
export function fireThreshold(card, catalog) {
  const full = lookupCatalog(card, catalog);
  return Number(full?.fireThreshold || 0);
}

/** Filter a cemetery's card list down to Fire Minions. */
export function findDeadFireMinions(cemeteryCards, catalog) {
  return (cemeteryCards || []).filter((c) => isFireMinion(c, catalog));
}

/** Sum of (F) thresholds across the supplied card instances. */
export function sumFireThresholds(cards, catalog) {
  let sum = 0;
  for (const card of cards || []) sum += fireThreshold(card, catalog);
  return sum;
}

// ── Projectile path scan ───────────────────────────────────────────
//
// Cardinal-direction projectile per codex. Steps from `startCell`
// outward in each of the 4 cardinal directions. The first hittable
// unit found in a direction is a candidate target cell. Caller is
// responsible for region matching (surface-only is the typical case
// since Avatars are always surface) and Stealth filtering. They pass
// `isHittable(card)` that returns true only for valid impact units.
//
// `unitsByCell` maps `"col,row"` to an array of card instances
// occupying that cell. `startCell` is the shooter's cell. The shooter
// itself (and allies on the same cell) are ignored on step 0 per
// codex.
//
// Returns a `Set<"col,row">` of up-to-4 candidate impact cells.


export function projectileImpactCells({
  startCell,
  totalCols,
  totalRows,
  unitsByCell,
  isHittable,
}) {
  const impacts = new Set();
  for (const path of projectilePaths({ startCell, totalCols, totalRows, unitsByCell, isHittable })) {
    if (path.impactCell) impacts.add(`${path.impactCell.col},${path.impactCell.row}`);
  }
  return impacts;
}

/**
 * Per-direction projectile path data. For each cardinal direction with
 * a hittable unit in line of sight, returns:
 *   {
 *     direction: 'N' | 'S' | 'E' | 'W',
 *     pathCells: Array<{col,row}>,  // every cell the projectile crosses,
 *                                   // ending with the impact cell
 *     impactCell: {col,row},        // the cell the projectile lands on
 *   }
 * Directions where the projectile fizzles (reaches grid edge without
 * a hittable unit) are omitted. The shooter's start cell is NOT in
 * pathCells per codex ("Projectiles enter their starting location"
 * marks t=0; pathCells is the t>0 flight).
 */
